import { useCallback, useMemo, useRef, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { format } from 'date-fns';
import { ChevronDown, Download, Plus } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/atoms/alert-dialog';
import { Button } from '@/components/atoms/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/atoms/dropdown-menu';
import { TooltipProvider } from '@/components/atoms/tooltip';
import { WorkspaceShell, useWorkspaceHeader } from '@/components/layouts';
import { Fullbleed } from '@/contexts/fullbleed';
import { TimesheetAddEntrySheet } from '@/components/timesheet/TimesheetAddEntrySheet';
import { TimesheetEmployeeSheet } from '@/components/timesheet/TimesheetEmployeeSheet';
import { TimesheetLiveSessions } from '@/components/timesheet/TimesheetLiveSessions';
import { TimesheetPeriodBar } from '@/components/timesheet/TimesheetPeriodBar';
import { TimesheetSummary } from '@/components/timesheet/TimesheetSummary';
import { TimesheetTeamTable } from '@/components/timesheet/TimesheetTeamTable';
import { dateOf } from '@/components/timesheet/entry-time';
import {
  blockingOpenSession,
  countByStatusFilter,
  dayDate,
  filterAndSortRows,
  formatPeriodLabel,
  openSessionsByTechnician,
  parseWeekParam,
  payPeriodFor,
  type SortDirection,
  type TimesheetSortKey,
  type TimesheetStatusFilter,
} from '@/components/timesheet/timesheet-display';
import {
  useApproveTimesheet,
  useApproveTimesheets,
  useOpenClockSessions,
  useReopenTimesheet,
  useTimesheetExport,
  useTimesheetPeriod,
} from '@/hooks/timesheet';
import { useCrewMembers } from '@/hooks/crew/useCrewMembers';
import { useHasPermission } from '@/hooks/usePermissions';
import type { OpenClockSession, TimesheetPeriodRow } from '@/types/timesheet';

const STATUS_FILTERS: TimesheetStatusFilter[] = ['all', 'pending', 'approved', 'overtime'];

interface TimesheetSearch {
  /** Any day inside the pay period to show (yyyy-MM-dd); omitted = this week. */
  week?: string;
  status?: TimesheetStatusFilter;
}

export const Route = createFileRoute('/_dashboard/dashboard/timesheet')({
  validateSearch: (search: Record<string, unknown>): TimesheetSearch => ({
    week: parseWeekParam(search.week),
    status: STATUS_FILTERS.includes(search.status as TimesheetStatusFilter)
      ? (search.status as TimesheetStatusFilter)
      : undefined,
  }),
  component: TimesheetPage,
});

function TimesheetPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const period = useMemo(
    () => payPeriodFor(search.week ? dayDate(search.week) : new Date()),
    [search.week],
  );
  const status = search.status ?? 'all';
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState<TimesheetSortKey>('name');
  const [sortDir, setSortDir] = useState<SortDirection>('asc');

  const [employee, setEmployee] = useState<{
    technicianId: string;
    focusDate: string | null;
  } | null>(null);
  const [addTime, setAddTime] = useState<{ technicianId?: string } | null>(null);
  const [confirmApproveAll, setConfirmApproveAll] = useState(false);
  const [pendingTechIds, setPendingTechIds] = useState<ReadonlySet<string>>(new Set());

  const canApprove = useHasPermission('timesheets:approve');
  // Adding office entries needs timesheets:edit (matches the create endpoint),
  // but editing/deleting existing entries is allowed for calendar:edit too —
  // the PATCH/DELETE routes accept either permission, so the UI must as well.
  const canEdit = useHasPermission('timesheets:edit');
  const canCalendarEdit = useHasPermission('calendar:edit');
  const canEditEntries = canEdit || canCalendarEdit;
  // Export emits payroll data — the export endpoint also requires payroll:view.
  const canExport = useHasPermission('payroll:view');
  // Clock-out is a calendar:edit route.
  const canClockOut = canCalendarEdit;

  const periodQuery = useTimesheetPeriod({
    periodStart: period.startKey,
    periodEnd: period.endKey,
  });
  const rows = useMemo(() => periodQuery.data?.rows ?? [], [periodQuery.data]);
  const sessionsQuery = useOpenClockSessions(true, {
    refetchIntervalMs: 60_000,
    showErrorToast: false,
  });
  const sessionsByTech = useMemo(
    () => openSessionsByTechnician(sessionsQuery.data),
    [sessionsQuery.data],
  );
  const crewQuery = useCrewMembers({ limit: 200 });
  const crew = useMemo(() => crewQuery.data?.data ?? [], [crewQuery.data]);
  const timezoneById = useMemo(() => new Map(crew.map((t) => [t.id, t.timezone])), [crew]);
  const timezoneFor = useCallback((id: string) => timezoneById.get(id), [timezoneById]);
  const techOptions = useMemo(
    () =>
      crew.map((t) => ({
        id: t.id,
        label: `${t.firstName} ${t.lastName ?? ''}`.trim(),
        timezone: t.timezone,
      })),
    [crew],
  );

  const approve = useApproveTimesheet();
  const reopen = useReopenTimesheet();
  const bulk = useApproveTimesheets();
  const { exportCSV, isExporting } = useTimesheetExport();

  const visibleRows = useMemo(
    () => filterAndSortRows(rows, { query, status, sortKey, sortDir }),
    [rows, query, status, sortKey, sortDir],
  );
  const statusCounts = useMemo(() => countByStatusFilter(rows), [rows]);

  const approvable = useMemo(
    () =>
      rows.filter(
        (row) =>
          row.approval.status === 'OPEN' &&
          !blockingOpenSession(
            sessionsByTech.get(row.technicianId),
            period,
            timezoneFor(row.technicianId),
          ),
      ),
    [rows, sessionsByTech, period, timezoneFor],
  );
  const blockedCount = (periodQuery.data?.totals.needsApproval ?? 0) - approvable.length;

  function setPeriod(weekStart: Date) {
    const next = payPeriodFor(weekStart);
    void navigate({
      search: (prev) => ({
        ...prev,
        week: next.startKey === payPeriodFor(new Date()).startKey ? undefined : next.startKey,
      }),
      replace: true,
    });
  }

  function setStatus(next: TimesheetStatusFilter) {
    void navigate({
      search: (prev) => ({ ...prev, status: next === 'all' ? undefined : next }),
      replace: true,
    });
  }

  function handleSort(key: TimesheetSortKey) {
    if (key === sortKey) {
      setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    // Names read A→Z; numbers are most useful biggest-first.
    setSortDir(key === 'name' || key === 'status' ? 'asc' : 'desc');
  }

  function markPending(technicianId: string, pending: boolean) {
    setPendingTechIds((prev) => {
      const next = new Set(prev);
      if (pending) next.add(technicianId);
      else next.delete(technicianId);
      return next;
    });
  }

  function handleApprove(row: TimesheetPeriodRow) {
    markPending(row.technicianId, true);
    approve.mutate(
      { technicianId: row.technicianId, periodStart: period.startKey, periodEnd: period.endKey },
      { onSettled: () => markPending(row.technicianId, false) },
    );
  }

  function handleReopen(row: TimesheetPeriodRow) {
    markPending(row.technicianId, true);
    reopen.mutate(
      { technicianId: row.technicianId, periodStart: period.startKey, periodEnd: period.endKey },
      { onSettled: () => markPending(row.technicianId, false) },
    );
  }

  async function handleApproveAll() {
    setConfirmApproveAll(false);
    await bulk.approveAll(
      approvable.map((row) => ({
        technicianId: row.technicianId,
        technicianName: row.technicianName,
        periodStart: period.startKey,
        periodEnd: period.endKey,
      })),
    );
  }

  function openSession(session: OpenClockSession) {
    const day = dateOf(session.clockIn, timezoneFor(session.technicianId));
    if (payPeriodFor(dayDate(day)).startKey !== period.startKey) setPeriod(dayDate(day));
    setEmployee({ technicianId: session.technicianId, focusDate: day });
  }

  // Sheet subject: the period row when there is one, else the crew record (a
  // technician opened from the live list may have no time in this period yet).
  const employeeRow = employee
    ? (rows.find((r) => r.technicianId === employee.technicianId) ?? null)
    : null;
  const employeeTech = useMemo(() => {
    if (!employee) return null;
    const member = crew.find((t) => t.id === employee.technicianId);
    const name =
      employeeRow?.technicianName ??
      (member ? `${member.firstName} ${member.lastName ?? ''}`.trim() : undefined) ??
      sessionsByTech.get(employee.technicianId)?.technicianName ??
      'Technician';
    return { id: employee.technicianId, name, timezone: member?.timezone };
  }, [employee, employeeRow, crew, sessionsByTech]);
  const employeeSession = employee ? (sessionsByTech.get(employee.technicianId) ?? null) : null;

  const todayKey = format(new Date(), 'yyyy-MM-dd');
  const addDefaultDate =
    todayKey >= period.startKey && todayKey <= period.endKey ? todayKey : period.startKey;
  const handleAddTime = useCallback(() => setAddTime({}), []);
  const exportDisabled = !periodQuery.data || rows.length === 0 || isExporting;

  return (
    <WorkspaceShell>
      <Fullbleed />
      <TimesheetHeaderSlots
        canEdit={canEdit}
        canExport={canExport}
        onAddEntry={handleAddTime}
        exportCSV={exportCSV}
        periodStart={period.startKey}
        periodEnd={period.endKey}
        exportDisabled={exportDisabled}
        isExporting={isExporting}
      />

      <TooltipProvider delayDuration={200}>
        <div className="flex h-full min-h-0 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto bg-background">
            <div className="mx-auto w-full max-w-[1320px] space-y-9 px-5 pt-7 pb-24 sm:px-10 sm:pt-9 md:pb-12">
              <div className="space-y-8">
                <TimesheetPeriodBar
                  period={period}
                  onPeriodChange={setPeriod}
                  approvableCount={approvable.length}
                  approvingAll={bulk.isPending}
                  onApproveAll={canApprove ? () => setConfirmApproveAll(true) : undefined}
                />

                <TimesheetSummary
                  data={periodQuery.data}
                  loading={periodQuery.isLoading}
                  openSessions={sessionsQuery.data}
                  openSessionsLoading={sessionsQuery.isLoading}
                  openSessionsError={sessionsQuery.isError}
                />
              </div>

              <TimesheetLiveSessions
                sessions={sessionsQuery.data ?? []}
                timezoneFor={timezoneFor}
                canClockOut={canClockOut}
                onOpen={openSession}
              />

              <TimesheetTeamTable
                rows={visibleRows}
                totalRowCount={rows.length}
                period={period}
                loading={periodQuery.isLoading}
                fetching={periodQuery.isFetching}
                error={periodQuery.isError}
                onRetry={() => void periodQuery.refetch()}
                search={query}
                onSearchChange={setQuery}
                status={status}
                onStatusChange={setStatus}
                statusCounts={statusCounts}
                sortKey={sortKey}
                sortDir={sortDir}
                onSortChange={handleSort}
                onClearFilters={() => {
                  setQuery('');
                  setStatus('all');
                }}
                openSessions={sessionsByTech}
                timezoneFor={timezoneFor}
                pendingTechIds={pendingTechIds}
                canApprove={canApprove}
                canEdit={canEdit}
                onApprove={handleApprove}
                onReopen={handleReopen}
                onOpenEmployee={(technicianId, date) =>
                  setEmployee({ technicianId, focusDate: date ?? null })
                }
                onAddTime={(technicianId) => setAddTime({ technicianId })}
                emptyActions={
                  <>
                    {canEdit ? (
                      <Button size="sm" onClick={() => setAddTime({})}>
                        <Plus />
                        Add time
                      </Button>
                    ) : null}
                    {period.startKey !== payPeriodFor(new Date()).startKey ? (
                      <Button variant="outline" size="sm" onClick={() => setPeriod(new Date())}>
                        Go to this week
                      </Button>
                    ) : null}
                  </>
                }
              />

              {rows.length > 0 ? (
                <p className="text-xs text-muted-foreground">
                  Overtime follows each technician&apos;s payroll profile threshold. Travel is
                  included in the total. Select a name or a day to see clock-in and clock-out times.
                </p>
              ) : null}
            </div>
          </div>
        </div>

        <TimesheetEmployeeSheet
          open={employee !== null}
          onOpenChange={(open) => !open && setEmployee(null)}
          technician={employeeTech}
          row={employeeRow}
          period={period}
          focusDate={employee?.focusDate}
          openSession={employeeSession}
          approvalBlocked={Boolean(
            employeeTech &&
            blockingOpenSession(employeeSession ?? undefined, period, employeeTech.timezone),
          )}
          approvalPending={employee ? pendingTechIds.has(employee.technicianId) : false}
          canApprove={canApprove}
          canEdit={canEdit}
          canEditEntries={canEditEntries}
          canClockOut={canClockOut}
          onApprove={() => employeeRow && handleApprove(employeeRow)}
          onReopen={() => employeeRow && handleReopen(employeeRow)}
        />
      </TooltipProvider>

      <TimesheetAddEntrySheet
        open={addTime !== null}
        onOpenChange={(open) => !open && setAddTime(null)}
        technicians={techOptions}
        defaultDate={addDefaultDate}
        defaultTechnicianId={addTime?.technicianId}
      />

      <AlertDialog open={confirmApproveAll} onOpenChange={setConfirmApproveAll}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Approve {approvable.length} {approvable.length === 1 ? 'timesheet' : 'timesheets'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Hours for {formatPeriodLabel(period.start, period.end)} will be locked for payroll.
              You can reopen a timesheet later if something needs fixing.
              {blockedCount > 0
                ? ` ${blockedCount} ${blockedCount === 1 ? 'employee is' : 'employees are'} still on the clock and will be skipped.`
                : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void handleApproveAll()}>Approve</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </WorkspaceShell>
  );
}

interface TimesheetHeaderSlotsProps {
  canEdit: boolean;
  canExport: boolean;
  onAddEntry: () => void;
  exportCSV: (opts: {
    periodStart: string;
    periodEnd: string;
    approvedOnly: boolean;
  }) => void | Promise<void>;
  periodStart: string;
  periodEnd: string;
  exportDisabled: boolean;
  isExporting: boolean;
}

// Injects Timesheet's header into the global workspace header (mirrors
// Technicians). Each slot is memoized over primitives only — passing object/
// function identities through deps would loop the header slot state. Renders
// nothing itself.
function TimesheetHeaderSlots({
  canEdit,
  canExport,
  onAddEntry,
  exportCSV,
  periodStart,
  periodEnd,
  exportDisabled,
  isExporting,
}: TimesheetHeaderSlotsProps) {
  const exportRef = useRef(exportCSV);
  exportRef.current = exportCSV;
  const addRef = useRef(onAddEntry);
  addRef.current = onAddEntry;

  const contextHeader = useMemo(
    () => (
      <div className="min-w-0">
        <h1 className="truncate text-base font-semibold leading-tight">Timesheets</h1>
        <p className="truncate text-xs text-muted-foreground">
          Review hours, approve for payroll, and export
        </p>
      </div>
    ),
    [],
  );

  const newButton = useMemo(
    () =>
      canEdit ? (
        <Button size="sm" onClick={() => addRef.current()}>
          <Plus size={14} />
          <span>
            Add<span className="hidden sm:inline"> time</span>
          </span>
        </Button>
      ) : null,
    [canEdit],
  );

  // Export is payroll data: the backend export endpoint requires payroll:view
  // (on top of timesheets:view), so only surface the control when the user has
  // it — otherwise the button 403s on click.
  const toolbarActions = useMemo(
    () =>
      canExport ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" disabled={exportDisabled}>
              <Download size={14} />
              {isExporting ? 'Exporting…' : 'Export'}
              <ChevronDown size={14} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() => void exportRef.current({ periodStart, periodEnd, approvedOnly: true })}
            >
              Approved timesheets only
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() =>
                void exportRef.current({ periodStart, periodEnd, approvedOnly: false })
              }
            >
              All timesheets
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null,
    [canExport, periodStart, periodEnd, exportDisabled, isExporting],
  );

  useWorkspaceHeader({ contextHeader, toolbarActions, newButton }, [
    contextHeader,
    toolbarActions,
    newButton,
  ]);
  return null;
}
